import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import type { MarketListing } from '../../types';

// Shadcn/ui components
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from '@/components/ui/button';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"; // Usaremos Card como base
import { Separator } from '@/components/ui/separator';

// Icons from lucide-react
import {
  ArrowDownUp,
  Tag,
  Trash2,
  Edit3,
  Eye,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MapPin,
  Briefcase, // Para "Compra"
  UserCircle,
  MessageSquare // Para descrição, se precisar de ícone
} from 'lucide-react';

interface ListingCardProps {
  listing: MarketListing;
  onViewDetails: (id: string) => void;
  onEdit?: (listing: MarketListing) => void;
  onDelete?: (id: string) => void;
  onMarkAsCompleted?: (id: string) => void;
  showUserActions?: boolean;
  isAdmin?: boolean;
}

const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onViewDetails,
  onEdit,
  onDelete,
  onMarkAsCompleted,
  showUserActions,
  isAdmin
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: currentTheme } = useTheme();
  const [expanded, setExpanded] = useState(false);

  const isCurrentUserListing = user?.id === listing.user_id;

  const formattedPrice = listing.price !== null 
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(listing.price)
    : 'N/A';

  const getConditionText = () => {
    switch (listing.condition) {
      case 'new': return 'Novo';
      case 'like_new': return 'Como Novo';
      case 'good': return 'Bom';
      case 'fair': return 'Regular';
      case 'poor': return 'Ruim';
      default: return 'N/A';
    }
  };

  const handleUserProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que o clique no perfil expanda/recolha o card
    if (listing.user_profile?.username) {
      navigate(`/profile/${listing.user_profile.username}`);
    } else if (listing.user_id) {
      // Fallback se não houver username, mas houver user_id (embora menos ideal)
      navigate(`/profile/id/${listing.user_id}`);
    }
  };

  const toggleExpand = (e?: React.MouseEvent) => {
    // Se o clique foi num botão ou link dentro do card, não toggle
    if (e) {
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('a')) {
        return;
      }
    }
    setExpanded(!expanded);
  };
  
  const cardBgColor = currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = currentTheme === 'dark' ? 'text-gray-200' : 'text-gray-800';
  const subTextColor = currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const borderColor = currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200';
  const hoverBgColor = currentTheme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  const typeSpecificStyles = {
    sell: {
      icon: <Tag className="h-4 w-4 mr-1.5" />,
      label: 'Venda',
      badgeClass: `border-blue-500 text-blue-500 ${currentTheme === 'dark' ? 'dark:border-blue-400 dark:text-blue-400' : ''}`,
      headerBg: currentTheme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-500/10',
      priceColor: currentTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'
    },
    buy: {
      icon: <Briefcase className="h-4 w-4 mr-1.5" />,
      label: 'Compra',
      badgeClass: `border-purple-500 text-purple-500 ${currentTheme === 'dark' ? 'dark:border-purple-400 dark:text-purple-400' : ''}`,
      headerBg: currentTheme === 'dark' ? 'bg-purple-900/30' : 'bg-purple-500/10',
      priceColor: currentTheme === 'dark' ? 'text-purple-400' : 'text-purple-600'
    },
    trade: {
      icon: <ArrowDownUp className="h-4 w-4 mr-1.5" />,
      label: 'Troca',
      badgeClass: `border-green-500 text-green-500 ${currentTheme === 'dark' ? 'dark:border-green-400 dark:text-green-400' : ''}`,
      headerBg: currentTheme === 'dark' ? 'bg-green-900/30' : 'bg-green-500/10',
      priceColor: currentTheme === 'dark' ? 'text-green-400' : 'text-green-600'
    }
  };

  const currentTypeStyle = typeSpecificStyles[listing.listing_type] || typeSpecificStyles.sell; // Fallback para sell

  return (
    <Card 
      className={`w-full shadow-md rounded-lg overflow-hidden transition-shadow duration-200 ${cardBgColor} ${textColor} border ${borderColor}`}
      // Removido onClick daqui para permitir clique apenas no header para expandir, ou botões especificos
    >
      <CardHeader 
        className={`p-3 sm:p-4 flex flex-row items-start space-x-3 sm:space-x-4 ${currentTypeStyle.headerBg} cursor-pointer ${hoverBgColor}`}
        onClick={() => toggleExpand()} // Header clicável para expandir
      >
        <Avatar className="w-16 h-16 sm:w-20 sm:h-20 rounded-md border-2 border-white dark:border-gray-600 flex-shrink-0">
          <AvatarImage src={listing.game?.thumb_url || listing.game?.image_url || '/placeholder-game.png'} alt={listing.game?.name || 'Jogo'} className="object-contain" />
          <AvatarFallback className="bg-gray-300 dark:bg-gray-700 text-xs sm:text-sm">
            {listing.game?.name?.substring(0, 2).toUpperCase() || 'JG'}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-grow min-w-0">
          <CardTitle className={`text-base sm:text-lg font-bold truncate ${textColor}`} title={listing.game?.name || 'Jogo não especificado'}>
            {listing.game?.name || 'Jogo não especificado'}
          </CardTitle>
          
          <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-1.5">
            <Badge variant="outline" className={`text-xs px-2 py-0.5 ${currentTypeStyle.badgeClass}`}>
              {currentTypeStyle.icon}
              {currentTypeStyle.label}
            </Badge>
            {listing.condition && (
              <Badge variant="secondary" className={`text-xs px-2 py-0.5 ${currentTheme === 'dark' ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-200 text-gray-700 border-gray-300'}`}>
                {getConditionText()}
              </Badge>
            )}
          </div>

          {/* Preço e Perfil (visível no header em telas menores) */}
          <div className="sm:hidden mt-2 flex justify-between items-center">
            <p className={`text-lg font-semibold ${currentTypeStyle.priceColor}`}>{listing.listing_type === 'trade' ? 'Troca' : formattedPrice}</p>
            <div 
              className={`flex items-center space-x-1.5 cursor-pointer ${subTextColor} hover:underline`}
              onClick={handleUserProfileClick}
            >
              <Avatar className="w-5 h-5">
                <AvatarImage src={listing.user_profile?.avatar_url || undefined} />
                <AvatarFallback className="text-xs"><UserCircle className="w-3 h-3" /></AvatarFallback> {/* Ajustado tamanho ícone */} 
              </Avatar>
              <span className="text-xs truncate">{isCurrentUserListing ? 'Você' : (listing.user_profile?.username || 'Usuário')}</span>
            </div>
          </div>
        </div>

        {/* Preço e Perfil (visível no header à direita em telas maiores) */}
        <div className="hidden sm:flex flex-col items-end space-y-1 ml-auto flex-shrink-0 pl-2">
          <p className={`text-xl font-bold ${currentTypeStyle.priceColor}`}>{listing.listing_type === 'trade' ? 'Troca' : formattedPrice}</p>
          <div 
            className={`flex items-center space-x-1.5 cursor-pointer ${subTextColor} hover:underline`}
            onClick={handleUserProfileClick}
          >
            <Avatar className="w-6 h-6">
              <AvatarImage src={listing.user_profile?.avatar_url || undefined} />
              <AvatarFallback className="text-xs"><UserCircle className="w-4 h-4" /></AvatarFallback>
            </Avatar>
            <span className="text-sm truncate">{isCurrentUserListing ? 'Você' : (listing.user_profile?.username || 'Usuário')}</span>
          </div>
        </div>
        
        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); toggleExpand(e);}} className={`ml-2 sm:ml-0 self-start sm:self-center ${subTextColor} hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0`}>
          {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </Button>
      </CardHeader>

      {expanded && (
        <CardContent className={`p-3 sm:p-4 border-t ${borderColor} ${currentTheme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50/50' }`}>
          {(listing.description || listing.location || listing.city || listing.state || listing.country) && (
            <div className="mb-3 space-y-2">
              {listing.description && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase ${subTextColor} mb-0.5`}>Descrição</h4>
                  <p className={`text-sm whitespace-pre-wrap break-words ${textColor}`}>{listing.description}</p>
                </div>
              )}
              
              {(listing.location || listing.city || listing.state || listing.country) && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase ${subTextColor} mb-0.5 mt-2`}>Localização</h4>
                  <div className={`flex items-start text-sm ${textColor}`}>
                    <MapPin className={`h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0 ${subTextColor}`} />
                    <p>
                      {listing.location && <span className="block">{listing.location}</span>}
                      {listing.city || listing.state || listing.country ? (
                        <span className="block text-xs">
                          {[listing.city, listing.state, listing.country].filter(Boolean).join(' - ')}
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className={`text-xs ${subTextColor} mb-3 pt-2 border-t ${borderColor}`}>
            Publicado em: {new Date(listing.created_at).toLocaleDateString('pt-BR')} por {listing.user_profile?.username || 'Usuário'}
            {listing.updated_at !== listing.created_at && ` (Editado em: ${new Date(listing.updated_at).toLocaleDateString('pt-BR')})`}
          </div>

          <Separator className={`my-3 ${currentTheme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-1">
            <Button 
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onViewDetails(listing.id); }}
              className={`w-full sm:w-auto ${currentTheme === 'dark' ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              <Eye className="h-4 w-4 mr-1.5" /> Ver Detalhes
            </Button>
            
            {(showUserActions || isAdmin) && (
              <>
                {showUserActions && onEdit && (
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); onEdit(listing); }}
                    className={`w-full sm:w-auto ${currentTheme === 'dark' ? 'border-yellow-600 text-yellow-400 hover:bg-yellow-700/50' : 'border-yellow-500 text-yellow-600 hover:bg-yellow-100'}`}
                  >
                    <Edit3 className="h-4 w-4 mr-1.5" /> Editar
                  </Button>
                )}
                {showUserActions && onMarkAsCompleted && listing.status === 'active' && (
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); onMarkAsCompleted(listing.id); }}
                    className={`w-full sm:w-auto ${currentTheme === 'dark' ? 'border-green-600 text-green-400 hover:bg-green-700/50' : 'border-green-500 text-green-600 hover:bg-green-100'}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Concluído
                  </Button>
                )}
                {(showUserActions || isAdmin) && onDelete && (
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); onDelete(listing.id); }}
                    className={`w-full sm:w-auto text-red-600 border-red-500 hover:bg-red-500/10 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-500/20`}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> 
                    {isAdmin && !isCurrentUserListing && user?.id !== listing.user_id ? 'Admin Excluir' : 'Excluir'}
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ListingCard; 